import api from './api';
import { 
  STUDENT_AVAILABLE_TESTS, 
  TESTS, 
  TESTS_QUESTIONS,
  QUESTIONS,
  SUBJECTS, 
  ACADEMIC_YEARS, 
  BOARDS_EXAMS, 
  ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS,
  SUBJECT_TOPICS, 
  TESTS_GENERATE, 
  ASSIGNMENTS,
  ATTEMPT_START_MANAGED,
  attemptCompleteManagedUrl,
  STUDENT_START_TEST, 
  STUDENT_ATTEMPTS, 
  STUDENT_RESULTS, 
  STUDENT_RESULTS_DETAIL,
  STUDENT_ANALYTICS, 
  QUESTIONS_COUNT
} from '../config/Config';

const fetchAllPages = async (firstUrl) => {
  const rows = [];
  let nextUrl = firstUrl;

  while (nextUrl) {
    const response = await api.get(nextUrl);
    const data = response.data;
    if (Array.isArray(data)) {
      rows.push(...data);
      break;
    }
    rows.push(...(data?.results || []));
    nextUrl = data?.next || null;
  }

  return rows;
};

const testService = {
  // Get tests with attempt status for the current user. Single API request for the list.
  getTests: async (filters = {}) => {
    try {
      const start = performance.now();
      const response = await api.get(STUDENT_AVAILABLE_TESTS);
      const elapsed = performance.now() - start;
      const responseTimeHeader = response?.headers?.['x-api-response-time-seconds'];
      const queryCountHeader = response?.headers?.['x-query-count'];
      if (typeof console.info === 'function') {
        console.info(
          '[My Tests] API getTests:',
          `client_ms=${elapsed.toFixed(0)}`,
          responseTimeHeader != null ? `server_seconds=${responseTimeHeader}` : '',
          queryCountHeader != null ? `query_count=${queryCountHeader}` : ''
        );
      }
      // Extract tests from the response
      let tests = [];
      if (Array.isArray(response.data)) {
        tests = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        tests = response.data.results;
      } else if (response.data) {
        // Handle case where response.data is the array of tests
        tests = Object.values(response.data);
      }
      
      // Use actual question count from API; fallback to num_questions
      const getQuestionCount = (test) => {
        const fromApi = test.question_count ?? test.questions_count ?? test.num_questions ?? 0;
        return typeof fromApi === 'number' && fromApi > 0 ? fromApi : (test.num_questions ?? 0);
      };
      // Include all tests from API (do not filter by count so All/Assigned tabs can show supplement + correct counts)
      const enrichedTests = tests.map(test => {
        // Resolve target / due date
        const targetDate = test.target_date || test.assignment?.target_date || null;
        // Determine status based on attempt data
        let status = 'not-started';
        const hasAttempt = !!test.attempt_id;
        const isSubmitted = test.has_submitted_attempt || test.status==='SUBMITTED' || !!test.attempt_submitted_at || !!test.submitted_at;
        
        if (isSubmitted) {
          status = 'completed';
        } else if (hasAttempt) {
          status = 'in-progress';
        }
        
        const totalQs = getQuestionCount(test);
        
        return {
          id: test.id,
          title: test.title,
          description: test.description,
          duration: test.duration_minutes || test.duration,
          passing_score: test.passing_score,
          status,
          has_submitted_attempt: isSubmitted,
          has_any_attempt: hasAttempt,
          can_retake: !isSubmitted,
          attemptId: test.attempt_id,
          score: test.attempt_score,
          passed: test.passed,
          started_at: test.attempt_started_at,
          completedDate: isSubmitted ? (test.completed_date || test.attempt_end_time || test.attempt_submitted_at || test.submitted_at || test.completed_at) : null,
          progress: test.progress || 0,
          dueDate: targetDate || test.available_to || test.due_date,
          // Add date display properties
          dateLabel: isSubmitted ? 'Completed On' : 'Due On',
          displayDate: isSubmitted ? (test.completed_date || test.attempt_end_time || test.attempt_submitted_at || test.submitted_at || new Date().toISOString()) : (targetDate || test.available_to || test.due_date),
          totalQuestions: totalQs,
          // Include all original test properties
          ...test,
          // ensure these enrichment fields exist even if backend omitted
          topics_list: Array.isArray(test.topics_list) ? test.topics_list : (test.topics ? (Array.isArray(test.topics) ? test.topics : [test.topics]) : []),
          percent_basic: test.percent_basic ?? 0,
          percent_advanced: test.percent_advanced ?? 0,
        };
      });
      
      return enrichedTests;
    } catch (error) {
      throw error;
    }
  },
  
  // Get a single test by ID
  getTestById: async (testId, options = {}) => {
    try {
      const response = await api.get(`${TESTS}${testId}/`, options);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get test questions
  getTestQuestions: async (testId, options = {}) => {
    try {
      const response = await api.get(`${TESTS}${testId}/questions/`, options);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get subjects for test creation
  getSubjects: async () => {
    try {
      const response = await api.get(SUBJECTS);
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      throw error;
    }
  },

  // Get academic years for test creation
  getYears: async () => {
    try {
      const response = await api.get(ACADEMIC_YEARS + '?published_only=true');
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      throw error;
    }
  },

  // Get boards for test creation
  getBoards: async () => {
    try {
      const response = await api.get(BOARDS_EXAMS + '?published_only=true');
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      throw error;
    }
  },

  // Get topics for test creation
  getTopics: async () => {
    try {
      const response = await api.get(`${SUBJECT_TOPICS}?page_size=1000`);
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      throw error;
    }
  },

  /**
   * Subject-topic LOV from the same endpoint as auto test generation (optional filters match AutoTestDialog).
   * @param {object} opts
   * @param {string|number} [opts.subject] - subject id (server filters topics)
   * @param {string|number} [opts.board]
   * @param {string|number} [opts.academicYear]
   * @param {string|number} [opts.examMode] - mode id or 'practice'
   * @param {boolean} [opts.mineOnly]
   * @param {string|number} [opts.difficulty]
   * @param {number} [opts.pageSize=10000]
   */
  fetchSubjectTopics: async ({
    subject,
    board,
    academicYear,
    examMode,
    mineOnly = false,
    difficulty,
    pageSize = 10000,
  } = {}) => {
    const params = new URLSearchParams();
    params.append('page_size', String(pageSize));
    if (subject != null && subject !== '') {
      params.append('subject', String(subject));
    }
    if (board) {
      params.append('board', String(board));
    }
    if (academicYear) {
      params.append('academic_year', String(academicYear));
    }
    if (examMode != null && examMode !== '') {
      if (examMode !== 'practice') {
        params.append('exam_mode', String(examMode));
      } else {
        params.append('exam_mode', 'practice');
      }
    }
    if (mineOnly) {
      params.append('mine', '1');
    }
    if (difficulty != null && difficulty !== '') {
      params.append('difficulty', String(difficulty));
    }
    const response = await api.get(`${SUBJECT_TOPICS}?${params.toString()}`);
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  },

  /**
   * Topics available for test creation: active curriculum mappings intersected with
   * question counts for the selected board/year/subject filters.
   */
  fetchCurriculumSubjectTopics: async ({
    subject,
    board,
    academicYear,
    examMode,
    mineOnly = false,
    difficulty,
    pageSize = 10000,
  } = {}) => {
    if (!subject || !board || !academicYear) {
      return [];
    }

    const curriculumParams = new URLSearchParams();
    curriculumParams.append('page_size', String(pageSize));
    curriculumParams.append('subject', String(subject));
    curriculumParams.append('board', String(board));
    curriculumParams.append('academic_year', String(academicYear));
    curriculumParams.append('is_active', 'true');

    const [curriculumRows, countedTopics] = await Promise.all([
      fetchAllPages(`${ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS}?${curriculumParams.toString()}`),
      testService.fetchSubjectTopics({
        subject,
        board,
        academicYear,
        examMode,
        mineOnly,
        difficulty,
        pageSize,
      }),
    ]);

    const countsByTopicId = new Map(
      countedTopics.map((topic) => [String(topic.id), topic])
    );
    const parentMappingIds = new Set(
      curriculumRows
        .map((row) => row.parent_mapping)
        .filter((id) => id != null)
        .map((id) => String(id))
    );

    return curriculumRows
      .filter((row) => !parentMappingIds.has(String(row.id)))
      .map((row) => {
        const topicId = row.topic;
        if (topicId == null) return null;
        const counted = countsByTopicId.get(String(topicId));
        return {
          id: topicId,
          subject: row.subject,
          subject_name: row.subject_name,
          topic: row.topic_name || counted?.topic || `Topic ${topicId}`,
          name: row.topic_name || counted?.name || counted?.topic || `Topic ${topicId}`,
          topic_type: counted?.topic_type,
          parenttopic: counted?.parenttopic,
          parenttopic_name: counted?.parenttopic_name,
          question_count: counted?.question_count ?? 0,
          curriculum_mapping_id: row.id,
          display_order: row.display_order,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const orderA = Number.isFinite(Number(a.display_order)) ? Number(a.display_order) : 0;
        const orderB = Number.isFinite(Number(b.display_order)) ? Number(b.display_order) : 0;
        if (orderA !== orderB) return orderA - orderB;
        return String(a.topic).localeCompare(String(b.topic), undefined, { sensitivity: 'base' });
      });
  },

  // Create an auto-generated test
  createAutoTest: async (testData) => {
    try {
      const response = await api.post(TESTS_GENERATE, testData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Assign a test to students (or self-assign for students)
  assignTest: async (testId, studentIds = null, targetDate = null) => {
    try {
      const payload = {
        test: testId
      };
      
      // For students (self-assignment), don't include students array
      if (studentIds && studentIds.length > 0) {
        payload.students = studentIds;
      }
      
      if (targetDate) {
        payload.target_date = targetDate;
      }
      
      const response = await api.post(ASSIGNMENTS, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  /** Teacher: start or resume an attempt for a roster (not yet onboarded) student. */
  startManagedAttempt: async (testId, teacherManagedStudentId, mode = 'EXAM') => {
    const response = await api.post(ATTEMPT_START_MANAGED, {
      test: testId,
      teacher_managed_student_id: teacherManagedStudentId,
      mode,
    });
    return response.data;
  },

  /** Teacher: finalize roster/offline attempt after entering answers (no student submit). */
  completeManagedAttempt: async (attemptId) => {
    const response = await api.post(attemptCompleteManagedUrl(attemptId), {});
    return response.data;
  },

  // Start a new test attempt
  startTestAttempt: async (testId, mode = 'EXAM', force_fresh = false, options = {}) => {
    try {
      const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];
      const authToken = localStorage.getItem('token');

      // Include the mode and force_fresh in the request payload
      const response = await api.post(`${STUDENT_START_TEST}${testId}/start/`, {
        mode: mode,
        force_fresh: force_fresh
      }, options);

      return response.data;
    } catch (error) {
  
           const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          'Failed to start test attempt';
   
       const enhancedError = new Error(errorMessage);
       enhancedError.response = error.response;
       enhancedError.status = error.response?.status;
       

   
       throw enhancedError;
    }
  },
  
  
  // Save an answer for a question in a test attempt
  saveAnswerConfidence: async (attemptId, questionId, confidence) => {
    try {
      if (!attemptId) {
        throw new Error('No attempt ID provided');
      }
      
      // Check if an answer exists for this question and attempt
      let response;
      try {
        const endpoints = [
          '/exams/answers/',
          `/exams/student/attempts/${attemptId}/answers/`
        ];
        
        let checkResponse;
        let lastError;
        
        for (const endpoint of endpoints) {
          try {
            checkResponse = await api.get(
              endpoint,
              {
                params: { 
                  question: questionId,
                  test_attempt: attemptId
                },
                validateStatus: () => true
              }
            );
            
            if (checkResponse.status === 200) {
              break;
            } else {
              lastError = new Error(`Request failed with status ${checkResponse.status}`);
              lastError.response = checkResponse;
            }
          } catch (err) {
            lastError = err;
          }
        }
        
        if (!checkResponse || checkResponse.status !== 200) {
          throw lastError || new Error('Failed to check for existing answers');
        }
        
        let answers = [];
        if (checkResponse.data) {
          if (Array.isArray(checkResponse.data)) {
            answers = checkResponse.data;
          } else if (checkResponse.data.results) {
            answers = checkResponse.data.results;
          } else if (checkResponse.data.id) {
            answers = [checkResponse.data];
          }
        }
        
        const existingAnswer = answers.find(a => 
          a.question === questionId || a.question?.id === questionId
        );
        
        if (existingAnswer && existingAnswer.id) {
          // Update existing answer with confidence
          const endpointToUse = `/exams/answers/${existingAnswer.id}/`;
          response = await api.patch(
            endpointToUse,
            { confidence: confidence },
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              validateStatus: () => true
            }
          );
        } else {
          // Answer doesn't exist yet - can't save confidence without answer
          // This is okay, confidence will be saved on submit
          return;
        }
        
        if (response.status >= 400) {
          console.error('Failed to save confidence:', response.data);
        }
      } catch (error) {
        console.error('Error saving confidence:', error);
        // Don't throw - confidence will be saved on submit
      }
    } catch (error) {
      console.error('Error in saveAnswerConfidence:', error);
      // Don't throw - confidence will be saved on submit
    }
  },

  saveAnswer: async (attemptId, questionId, answer, subAnswersData = null) => {
    try {
      if (!attemptId) {
        throw new Error('No attempt ID provided');
      }
      

      
      // First, check if an answer already exists for this question and attempt
      let response;
      try {

        
        // Try both endpoints to be safe
        const endpoints = [
          '/exams/answers/',
          `/exams/student/attempts/${attemptId}/answers/`
        ];
        
        let checkResponse;
        let lastError;
        
        for (const endpoint of endpoints) {
          try {

            checkResponse = await api.get(
              endpoint,
              {
                params: { 
                  question: questionId,
                  test_attempt: attemptId
                },
                validateStatus: () => true // Don't throw on any status
              }
            );
            
            if (checkResponse.status === 200) {

              break;
            } else {

              lastError = new Error(`Request failed with status ${checkResponse.status}`);
              lastError.response = checkResponse;
            }
          } catch (err) {

            lastError = err;
          }
        }
        
        if (!checkResponse || checkResponse.status !== 200) {
          throw lastError || new Error('Failed to check for existing answers');
        }
        
        // Handle different response formats
        let answers = [];
        if (checkResponse.data) {
          if (Array.isArray(checkResponse.data)) {
            answers = checkResponse.data;
          } else if (checkResponse.data.results) {
            answers = checkResponse.data.results;
          } else if (checkResponse.data.id) {
            answers = [checkResponse.data];
          }
        }
        
        const existingAnswer = answers.find(a => 
          a.question === questionId || a.question?.id === questionId
        );
        
        // Prepare the payload according to the backend's expected format
        const payload = {
          answer: (() => {
            if (answer === undefined || answer === null) return '';
            if (typeof answer === 'boolean') return answer ? 'true' : 'false';
            return answer.toString();
          })(),
          question: Number(questionId)
        };
        
        // Add sub_answers_data if provided (for questions with sub-questions)
        if (subAnswersData && Array.isArray(subAnswersData) && subAnswersData.length > 0) {
          payload.sub_answers_data = subAnswersData;
        }
        

        
        // Validate payload
        if (isNaN(payload.question)) {
          throw new Error(`Invalid question ID (${payload.question})`);
        }
        
        let endpointToUse;
        if (existingAnswer && existingAnswer.id) {
          // Update existing answer
          const updatePayload = { answer: payload.answer };
          if (payload.sub_answers_data) {
            updatePayload.sub_answers_data = payload.sub_answers_data;
          }
          
          endpointToUse = `/exams/answers/${existingAnswer.id}/`;
          response = await api.patch(
            endpointToUse,
            updatePayload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              validateStatus: () => true // Don't throw on any status
            }
          );
        } else {
          // Create new answer

          endpointToUse = `/exams/student/attempts/${attemptId}/answers/`;
          response = await api.post(
            endpointToUse,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              validateStatus: () => true // Don't throw on any status
            }
          );
        }
        

        
        if (response.status >= 400) {
          const errorData = {
            status: response.status,
            data: response.data,
            endpoint: endpointToUse,
            method: existingAnswer ? 'PATCH' : 'POST',
            payload,
            headers: response.headers
          };
          

          
          let errorMessage = 'Failed to save answer';
          try {
            if (response.data) {
              if (typeof response.data === 'string') {
                errorMessage = response.data;
              } else if (response.data.detail) {
                errorMessage = response.data.detail;
              } else if (response.data.error) {
                errorMessage = Array.isArray(response.data.error) 
                  ? response.data.error.join(', ')
                  : response.data.error;
              } else if (response.data.message) {
                errorMessage = response.data.message;
              } else if (typeof response.data === 'object') {
                // Handle field-specific errors
                const fieldErrors = Object.entries(response.data)
                  .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                  .join('; ');
                if (fieldErrors) errorMessage = fieldErrors;
              } else {
                errorMessage = JSON.stringify(response.data);
              }
            }
          } catch (e) {
            // Error parsing error response
          }
          
          const error = new Error(errorMessage);
          error.details = errorData;
          error.response = response;
          throw error;
        }
        
        return response.data;
        
      } catch (error) {
        console.error('[testService] Error in saveAnswer:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          endpoint: error.config?.url,
          method: error.config?.method,
          requestData: error.config?.data ? JSON.parse(error.config.data) : null,
          stack: error.stack
        });
        
        // Create a more user-friendly error
        const friendlyError = new Error('Failed to save answer. Please try again.');
        friendlyError.originalError = error;
        throw friendlyError;
      }
    } catch (error) {
      const errorDetails = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        request: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data ? JSON.parse(error.config.data) : null,
          headers: error.config?.headers
        },
      };
      

      
      // Create a more detailed error message
      const serverError = new Error(
        error.response?.data?.detail || 
        error.response?.data?.error?.[0] || 
        'Failed to save answer. Please try again.'
      );
      
      // Attach additional context to the error
      Object.assign(serverError, errorDetails);
      
      throw serverError;
    }
  },
  
  // Get a specific test attempt
  getTestAttempt: async (attemptId, options = {}) => {
    try {
      const response = await api.get(`${STUDENT_RESULTS_DETAIL}${attemptId}/`, options);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get existing attempts for a test
  getTestAttempts: async (testId, options = {}) => {
    try {
      const response = await api.get(`${STUDENT_ATTEMPTS}?test_id=${testId}`, options);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Load test and questions with full details for the View Test / View questions dialog.
   * Used by List of Tests and Student Assignments. Returns { test, questions }.
   */
  loadTestQuestionsForView: async (testId) => {
    const [testRes, qRes] = await Promise.all([
      api.get(`${TESTS}${testId}/`),
      api.get(`${TESTS_QUESTIONS}${testId}/questions/`)
    ]);
    const test = testRes.data;
    const questionsList = Array.isArray(qRes.data) ? qRes.data : (qRes.data?.results || []);
    const questions = await Promise.all(
      questionsList.map(async (q) => {
        try {
          const questionRes = await api.get(`${QUESTIONS}${q.id}/`);
          return {
            ...q,
            ...questionRes.data,
            question_text: q.question_text || q.question || q.text || '',
            difficulty: q.difficulty || q.level || 'Unknown',
            options: questionRes.data.options || q.options || {}
          };
        } catch (e) {
          return {
            ...q,
            question_text: q.question_text || q.question || q.text || '',
            difficulty: q.difficulty || q.level || 'Error',
            options: q.options || {}
          };
        }
      })
    );
    return { test, questions };
  },

  // Restart a test attempt
  restartTestAttempt: async (attemptId) => {
    try {
      const response = await api.post(`${STUDENT_ATTEMPTS}${attemptId}/restart/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  

  // Get test results
  getTestResults: async (attemptId) => {
    try {
      // Fixed: Added /results/ to match Django URL pattern
      const response = await api.get(`${STUDENT_RESULTS}${attemptId}/results/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Get user's test analytics
  getUserAnalytics: async () => {
    try {
      const response = await api.get(STUDENT_ANALYTICS);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Submit a test attempt with answers
  submitTestAttempt: async (attemptId, submissionData) => {
    try {
      const response = await api.post(
        `/exams/student/attempts/${attemptId}/submit/`,
        submissionData
      );
      
      return response.data;
      
    } catch (error) {
      
      // Create a more informative error
      const errorMessage = error.response?.data?.error?.[0] || 
                         error.response?.data?.detail || 
                         'Failed to submit test attempt. Please try again.';
      
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      enhancedError.status = error.response?.status;
      
      throw enhancedError;
    }
  },

  /**
   * Download test worksheet PDF. Centralized for use by teacher and student (View Questions dialog,
   * Instructions page, Resume dialog). Returns nothing on success; throws on error.
   * @param {number} testId - Test ID
   * @param {string} [filename='worksheet'] - Filename without extension (e.g. test title)
   */
  downloadTestWorksheet: async (testId, filename = 'worksheet') => {
    if (!testId) throw new Error('Test ID is required');
    const response = await api.get(`${TESTS}${testId}/worksheet/`, { responseType: 'blob' });
    if (response.status !== 200) {
      const err = new Error(`Failed to download worksheet: ${response.status} ${response.statusText}`);
      err.response = response;
      throw err;
    }
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename || 'worksheet'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Get question counts by criteria for auto test dialog
  async getQuestionCounts(criteria) {
    try {
      const params = new URLSearchParams();
      
      if (criteria.subject) params.append('subject', criteria.subject);
      if (criteria.board) params.append('board', criteria.board);
      if (criteria.academic_year) params.append('academic_year', criteria.academic_year);
      if (criteria.topics && criteria.topics.length > 0) {
        criteria.topics.forEach((topic) => {
          const id = topic != null && typeof topic === 'object' ? topic.id : topic;
          if (id !== undefined && id !== null && String(id).trim() !== '') {
            params.append('topics', String(id));
          }
        });
      }
      if (criteria.mine_only !== undefined) {
        params.append('mine_only', criteria.mine_only ? 'true' : 'false');
      }
      if (criteria.exam_mode !== undefined) {
        const modeVal = String(criteria.exam_mode).toLowerCase() === 'practice' || criteria.exam_mode === '' ? 'practice' : criteria.exam_mode;
        params.append('exam_mode', modeVal);
      }
      
      const response = await api.get(`${QUESTIONS_COUNT}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default testService;
